import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  ClipboardList, Search, Plus,
  DollarSign, Package,
  Upload, FileText, X, Eye, Download,
  CheckCircle, AlertTriangle, Wrench, Activity, ArrowUpDown, ArrowUp, ArrowDown, Filter,
  History, Clock, MapPin, User, Calendar
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PurchasingDemirbaslar = ({ readOnly = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [demirbaslar, setDemirbaslar] = useState([]);
  const [branches, setBranches] = useState([]);
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDemirbas, setEditingDemirbas] = useState(null);
  const [selectedDemirbas, setSelectedDemirbas] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryDemirbas, setSelectedHistoryDemirbas] = useState(null);
  
  // Filtreleme ve Sıralama State'leri
  const [statusFilterTerm, setStatusFilterTerm] = useState('');
  const [statusLocationFilter, setStatusLocationFilter] = useState('all');
  const [listSortConfig, setListSortConfig] = useState({ key: null, direction: 'asc' });
  const [statusSortConfig, setStatusSortConfig] = useState({ key: null, direction: 'asc' });
  const [zimmetSearchTerm, setZimmetSearchTerm] = useState('');
  const [zimmetSortConfig, setZimmetSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const handleZimmetSort = (key) => {
    setZimmetSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getZimmetSortIcon = (key) => {
    if (zimmetSortConfig.key !== key) return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50 inline" />;
    return zimmetSortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 inline" /> 
      : <ArrowDown className="h-4 w-4 ml-1 inline" />;
  };
  
  const [statusFormData, setStatusFormData] = useState({
    location: '',
    location_other: '',
    usage_status: '',
    inventory_date: '',
    last_status: '',
    last_status_documents: [],
    last_status_description: ''
  });
  const [formData, setFormData] = useState({
    product_name: '',
    quantity: 1,
    unit: 'adet',
    purchase_price: 0,
    products: [{ product_name: '', quantity: 1, unit: 'adet', purchase_price: 0, brand: '', serial_number: '' }],
    description: '',
    serial_number: '',
    supplier_id: '',
    supplier: '',
    brand: '',
    location: '',
    usage_status: '',
    inventory_date: '',
    last_status: '',
    last_status_documents: [],
    last_status_description: '',
    status: 'active',
    documents: [],
    invoice_documents: []
  });

  // Çoklu ürün yardımcıları (Yeni Demirbaş modalı)
  const emptyProduct = () => ({ product_name: '', quantity: 1, unit: 'adet', purchase_price: 0, brand: '', serial_number: '' });
  const addProduct = () => {
    setFormData(prev => ({ ...prev, products: [...(prev.products || []), emptyProduct()] }));
  };
  const removeProduct = (index) => {
    setFormData(prev => {
      const products = (prev.products || []).filter((_, i) => i !== index);
      return { ...prev, products: products.length ? products : [emptyProduct()] };
    });
  };
  const updateProduct = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      products: (prev.products || []).map((p, i) => i === index ? { ...p, [field]: value } : p)
    }));
  };

  const [zimmetler, setZimmetler] = useState([]);
  const [zimmetLoading, setZimmetLoading] = useState(false);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  const fetchDemirbaslar = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/purchasing/demirbaslar`, {
        headers: getAuthHeaders()
      });
      setDemirbaslar(response.data);
    } catch (error) {
      console.error('Demirbaşlar yüklenirken hata:', error);
      toast.error('Demirbaşlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchZimmetler = useCallback(async () => {
    try {
      setZimmetLoading(true);
      const response = await axios.get(`${API}/api/depot/zimmetler`, {
        headers: getAuthHeaders()
      });
      setZimmetler(response.data || []);
    } catch (error) {
      console.error('Zimmetler yüklenirken hata:', error);
    } finally {
      setZimmetLoading(false);
    }
  }, []);

  const fetchBranches = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/branches`, {
        headers: getAuthHeaders()
      });
      setBranches(response.data);
    } catch (error) {
      console.error('Şubeler yüklenirken hata:', error);
    }
  }, []);

  const fetchCariHesaplar = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/accounting/cari-hesaplar`, {
        headers: getAuthHeaders()
      });
      setCariHesaplar(response.data);
    } catch (error) {
      console.error('Cari hesaplar yüklenirken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchDemirbaslar();
    fetchZimmetler();
    fetchBranches();
    fetchCariHesaplar();
  }, [fetchDemirbaslar, fetchZimmetler, fetchBranches, fetchCariHesaplar]);

  // URL parametrelerinden modal açma
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenModal = searchParams.get('openAddModal') === 'true';
    const urlJobId = searchParams.get('jobId');
    const urlJobName = searchParams.get('jobName');
    
    // localStorage'dan backup değerleri al
    const storedJobId = localStorage.getItem('draft_source_job_id');
    const storedJobName = localStorage.getItem('draft_source_job_name');
    
    // URL veya localStorage'dan final değerleri belirle
    const finalJobId = urlJobId || storedJobId;
    const finalJobName = urlJobName || storedJobName;
    
    if ((shouldOpenModal || storedJobId) && !loading) {
      // localStorage'dan teklif verisini al (offer prefill için)
      let jobData = null;
      try {
        const raw = localStorage.getItem('draft_source_job_data');
        if (raw) jobData = JSON.parse(raw);
      } catch (e) { jobData = null; }

      // localStorage'ı temizle
      localStorage.removeItem('draft_source_job_id');
      localStorage.removeItem('draft_source_job_name');
      localStorage.removeItem('draft_source_job_data');

      const baseForm = {
        product_name: '',
        quantity: 1,
        unit: 'adet',
        purchase_price: 0,
        products: [{ product_name: '', quantity: 1, unit: 'adet', purchase_price: 0, brand: '', serial_number: '' }],
        description: finalJobName ? `${decodeURIComponent(finalJobName)} işi için demirbaş` : '',
        serial_number: '',
        supplier_id: '',
        supplier: '',
        brand: '',
        location: '',
        usage_status: '',
        inventory_date: '',
        last_status: '',
        last_status_documents: [],
        last_status_description: '',
        status: 'active',
        documents: [],
        invoice_documents: [],
        related_job_id: finalJobId || '',
        related_job_name: finalJobName ? decodeURIComponent(finalJobName) : ''
      };

      // Yeni demirbaş modalını aç ve iş bilgilerini doldur
      setEditingDemirbas(null);
      setFormData(baseForm);
      setIsModalOpen(true);

      // Seçilen teklif varsa, ürünleri otomatik doldur
      const offerId = jobData?.selected_offer_id || jobData?.selected_teklif?.offer_id || null;
      if (offerId) {
        (async () => {
          try {
            const r = await axios.get(`${API}/api/purchasing/offers/${offerId}`, { headers: getAuthHeaders() });
            const offer = r.data;
            if (offer?.items?.length) {
              const prefillProducts = offer.items.map(item => ({
                product_name: item.product_name || '',
                quantity: item.quantity != null ? item.quantity : 1,
                unit: item.unit || 'adet',
                purchase_price: item.price != null ? item.price : 0,
                brand: item.model || '',
                serial_number: ''
              }));
              setFormData(prev => ({
                ...prev,
                products: prefillProducts,
                supplier_id: offer.cari_hesap_id || prev.supplier_id,
                supplier: offer.cari_hesap_name || prev.supplier
              }));
            }
          } catch (e) {
            console.error('Teklif ürünleri çekilemedi:', e);
          }
        })();
      }

      // URL'i temizle
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search, loading]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('tr-TR');
    } catch {
      return '-';
    }
  };

  // File upload handler for main form
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newDocuments = [];
    
    for (const file of files) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newDocuments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Dosya yükleme hatası:', error);
        toast.error(`${file.name} yüklenemedi`);
      }
    }

    setFormData(prev => ({
      ...prev,
      documents: [...prev.documents, ...newDocuments]
    }));
    toast.success(`${newDocuments.length} belge eklendi`);
  };

  // File upload handler for invoice documents
  const handleInvoiceFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newDocuments = [];
    
    for (const file of files) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newDocuments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Dosya yükleme hatası:', error);
        toast.error(`${file.name} yüklenemedi`);
      }
    }

    setFormData(prev => ({
      ...prev,
      invoice_documents: [...prev.invoice_documents, ...newDocuments]
    }));
    toast.success(`${newDocuments.length} fatura belgesi eklendi`);
  };

  const handleRemoveInvoiceDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      invoice_documents: prev.invoice_documents.filter((_, i) => i !== index)
    }));
  };

  // File upload handler for status form
  const handleStatusFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newDocuments = [];
    
    for (const file of files) {
      try {
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        newDocuments.push({
          name: file.name,
          size: file.size,
          type: file.type,
          data: base64,
          uploaded_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Dosya yükleme hatası:', error);
        toast.error(`${file.name} yüklenemedi`);
      }
    }

    setStatusFormData(prev => ({
      ...prev,
      last_status_documents: [...prev.last_status_documents, ...newDocuments]
    }));
    toast.success(`${newDocuments.length} belge eklendi`);
  };

  const handleRemoveDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  const handleRemoveStatusDocument = (index) => {
    setStatusFormData(prev => ({
      ...prev,
      last_status_documents: prev.last_status_documents.filter((_, i) => i !== index)
    }));
  };

  const handleOpenModal = (demirbas = null) => {
    if (demirbas) {
      setEditingDemirbas(demirbas);
      setFormData({
        product_name: demirbas.product_name || '',
        quantity: demirbas.quantity || 1,
        unit: demirbas.unit || 'adet',
        purchase_price: demirbas.purchase_price || 0,
        description: demirbas.description || '',
        serial_number: demirbas.serial_number || '',
        supplier_id: demirbas.supplier_id || '',
        supplier: demirbas.supplier || '',
        brand: demirbas.brand || '',
        location: demirbas.location || '',
        usage_status: demirbas.usage_status || '',
        inventory_date: demirbas.inventory_date || '',
        last_status: demirbas.last_status || '',
        last_status_documents: demirbas.last_status_documents || [],
        last_status_description: demirbas.last_status_description || '',
        status: demirbas.status || 'active',
        documents: demirbas.documents || [],
        invoice_documents: demirbas.invoice_documents || []
      });
    } else {
      setEditingDemirbas(null);
      setFormData({
        product_name: '',
        quantity: 1,
        unit: 'adet',
        purchase_price: 0,
        products: [{ product_name: '', quantity: 1, unit: 'adet', purchase_price: 0, brand: '', serial_number: '' }],
        description: '',
        serial_number: '',
        supplier_id: '',
        supplier: '',
        brand: '',
        location: '',
        usage_status: '',
        inventory_date: '',
        last_status: '',
        last_status_documents: [],
        last_status_description: '',
        status: 'active',
        documents: [],
        invoice_documents: []
      });
    }
    setIsModalOpen(true);
  };

  const handleOpenStatusModal = (demirbas) => {
    setSelectedDemirbas(demirbas);
    const isOtherLocation = demirbas.location && !branches.some(b => b.name === demirbas.location);
    setStatusFormData({
      location: isOtherLocation ? 'other' : (demirbas.location || ''),
      location_other: isOtherLocation ? demirbas.location : '',
      usage_status: demirbas.usage_status || '',
      inventory_date: demirbas.inventory_date ? demirbas.inventory_date.split('T')[0] : '',
      last_status_documents: demirbas.last_status_documents || [],
      last_status_description: demirbas.last_status_description || ''
    });
    setShowStatusModal(true);
  };

  // Durum geçmişi yükleme fonksiyonu
  const loadStatusHistory = async (demirbas) => {
    setSelectedHistoryDemirbas(demirbas);
    setHistoryLoading(true);
    setShowHistoryModal(true);
    
    try {
      const isZimmet = demirbas.is_demirbas !== undefined;
      const historyUrl = isZimmet 
        ? `${API}/api/purchasing/demirbaslar/${demirbas.id}/history` 
        : `${API}/api/purchasing/demirbaslar/${demirbas.id}/history`;
        
      const response = await axios.get(historyUrl, { headers: getAuthHeaders() });
      setHistoryData(response.data || []);
    } catch (error) {
      console.error('Durum geçmişi yüklenirken hata:', error);
      // If no history exists, try fallback if it's an old zimmet
      if (error.response?.status === 404) {
         try {
           const fallbackRes = await axios.get(`${API}/api/depot/zimmetler/${demirbas.id}/gecmis`, { headers: getAuthHeaders() });
           setHistoryData(fallbackRes.data || []);
         } catch (fallbackError) {
           setHistoryData([{
             id: 'creation',
             date: demirbas.created_at,
             user_name: demirbas.created_by_name || 'Bilinmiyor',
             changes: ['Demirbaş/Zimmet oluşturuldu'],
             status: demirbas.durum || 'active',
             description: 'İlk kayıt'
           }]);
         }
      } else {
        setHistoryData([{
          id: 'creation',
          date: demirbas.created_at,
          user_name: demirbas.created_by_name || 'Bilinmiyor',
          changes: ['Demirbaş/Zimmet oluşturuldu'],
          status: demirbas.durum || 'active',
          description: 'İlk kayıt'
        }]);
      }
    }
    setHistoryLoading(false);
  };

  const handleSubmit = async () => {
    // Düzenleme modu: tek ürün (mevcut davranış)
    if (editingDemirbas) {
      if (!formData.product_name.trim()) {
        toast.error('Ürün adı gereklidir');
        return;
      }
      try {
        await axios.put(`${API}/api/purchasing/demirbaslar/${editingDemirbas.id}`, formData, {
          headers: getAuthHeaders()
        });
        toast.success('Demirbaş güncellendi');
        setIsModalOpen(false);
        fetchDemirbaslar();
      } catch (error) {
        console.error('İşlem hatası:', error);
        toast.error('Demirbaş güncellenemedi');
      }
      return;
    }

    // Yeni kayıt modu: çoklu ürün
    const validProducts = (formData.products || []).filter(p => (p.product_name || '').trim());
    if (validProducts.length === 0) {
      toast.error('En az bir ürün adı gereklidir');
      return;
    }

    const sharedFields = {
      supplier_id: formData.supplier_id,
      supplier: formData.supplier,
      description: formData.description,
      status: formData.status,
      location: formData.location,
      usage_status: formData.usage_status,
      documents: formData.documents,
      invoice_documents: formData.invoice_documents
    };

    try {
      // İş'den gelindiyse SADECE taslak kaydet - gerçek demirbaş kaydı yönetim
      // onayından sonra (finalize) oluşturulur. Erken/çift kayıt olmasın.
      if (formData.related_job_id) {
        await axios.post(`${API}/api/purchasing/jobs/${formData.related_job_id}/save-draft`, {
          draft_type: 'demirbas',
          draft_data: { ...sharedFields, products: validProducts, related_job_id: formData.related_job_id }
        }, { headers: getAuthHeaders() });
        toast.success(`${validProducts.length} demirbaş taslağı kaydedildi. Yönetim onayından sonra demirbaşlara eklenecektir.`);
        setIsModalOpen(false);
        fetchDemirbaslar();
        return;
      }

      // İş dışı doğrudan ekleme: gerçek demirbaş kaydı oluştur
      for (const p of validProducts) {
        const payload = {
          ...sharedFields,
          product_name: p.product_name,
          quantity: p.quantity || 1,
          unit: p.unit || 'adet',
          purchase_price: p.purchase_price || 0,
          brand: p.brand || '',
          serial_number: p.serial_number || ''
        };
        await axios.post(`${API}/api/purchasing/demirbaslar`, payload, { headers: getAuthHeaders() });
      }
      toast.success(`${validProducts.length} demirbaş eklendi`);
      setIsModalOpen(false);
      fetchDemirbaslar();
    } catch (error) {
      console.error('İşlem hatası:', error);
      toast.error('İşlem başarısız');
    }
  };

  const handleStatusSubmit = async () => {
    if (!selectedDemirbas) return;

    try {
      const finalLocation = statusFormData.location === 'other' 
        ? statusFormData.location_other 
        : statusFormData.location;

      const updateData = {
        location: finalLocation,
        usage_status: statusFormData.usage_status,
        inventory_date: statusFormData.inventory_date,
        last_status_documents: statusFormData.last_status_documents,
        last_status_description: statusFormData.last_status_description
      };

      await axios.put(`${API}/api/purchasing/demirbaslar/${selectedDemirbas.id}`, updateData, {
        headers: getAuthHeaders()
      });
      toast.success('Demirbaş durumu güncellendi');
      setShowStatusModal(false);
      fetchDemirbaslar();
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('Güncelleme başarısız');
    }
  };

  const handleViewDetail = (demirbas) => {
    setSelectedDemirbas(demirbas);
    setShowDetailModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Aktif</Badge>;
      case 'maintenance':
        return <Badge className="bg-yellow-100 text-yellow-700">Bakımda</Badge>;
      case 'disposed':
        return <Badge className="bg-red-100 text-red-700">Elden Çıkarıldı</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{status}</Badge>;
    }
  };

  const filteredDemirbaslar = demirbaslar.filter(d =>
    d.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.supplier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.cari_hesap_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sıralama fonksiyonu
  const sortData = (data, sortConfig) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Null/undefined değerler için
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // Sayısal değerler için
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // String değerler için
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Sıralama toggle fonksiyonu
  const handleListSort = (key) => {
    setListSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusSort = (key) => {
    setStatusSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredZimmetler = useMemo(() => {
    let result = zimmetler;
    
    if (zimmetSearchTerm) {
      const term = zimmetSearchTerm.toLowerCase();
      result = result.filter(z => 
        (z.urun_adi || '').toLowerCase().includes(term) ||
        (z.marka || '').toLowerCase().includes(term) ||
        (z.model || '').toLowerCase().includes(term) ||
        (z.seri_no || '').toLowerCase().includes(term) ||
        (z.kullanici_adi || '').toLowerCase().includes(term)
      );
    }
    
    if (statusLocationFilter !== 'all') {
      result = result.filter(z => z.location === statusLocationFilter || z.branch_name === statusLocationFilter);
    }
    
    if (zimmetSortConfig.key) {
      result = [...result].sort((a, b) => {
        let aVal = a[zimmetSortConfig.key] || '';
        let bVal = b[zimmetSortConfig.key] || '';
        
        if (zimmetSortConfig.key === 'created_at' || zimmetSortConfig.key === 'zimmet_tarihi') {
          aVal = new Date(aVal || 0).getTime();
          bVal = new Date(bVal || 0).getTime();
        } else {
          aVal = String(aVal).toLowerCase();
          bVal = String(bVal).toLowerCase();
        }
        
        if (aVal < bVal) return zimmetSortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return zimmetSortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [zimmetler, zimmetSearchTerm, zimmetSortConfig, statusLocationFilter]);

  // Demirbaş Durumu için filtreleme
  const getStatusFilteredData = (items) => {
    let filtered = items;
    
    // Ürün adı filtresi
    if (statusFilterTerm) {
      const term = statusFilterTerm.toLowerCase();
      filtered = filtered.filter(d => 
        d.product_name?.toLowerCase().includes(term) ||
        d.serial_number?.toLowerCase().includes(term)
      );
    }
    
    // Konum filtresi
    if (statusLocationFilter !== 'all') {
      filtered = filtered.filter(d => d.location === statusLocationFilter);
    }
    
    return filtered;
  };

  const uniqueLocations = useMemo(() => {
    // Collect locations from both zimmetler and demirbaslar to be safe
    const locs = zimmetler.map(z => z.location || z.branch_name).filter(Boolean);
    return [...new Set(locs)].sort();
  }, [zimmetler]);

  // Excel indirme fonksiyonu - Demirbaş Listesi
  const exportListToExcel = () => {
    const data = sortData(filteredDemirbaslar, listSortConfig);
    
    const headers = ['Ürün Adı', 'Marka', 'Tedarikçi', 'Seri No', 'Miktar', 'Birim', 'Birim Fiyat', 'Toplam', 'Durum', 'Tarih'];
    const rows = data.map(d => [
      d.product_name || '',
      d.brand || '',
      d.supplier || d.cari_hesap_name || '',
      d.serial_number || '',
      d.quantity || 1,
      d.unit || 'adet',
      d.purchase_price || 0,
      d.total_price || 0,
      d.status === 'active' ? 'Aktif' : d.status === 'maintenance' ? 'Bakımda' : d.status === 'disposed' ? 'Elden Çıkarıldı' : 'Aktif',
      d.created_at ? formatDate(d.created_at) : ''
    ]);
    
    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `demirbas_listesi_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Demirbaş listesi indirildi');
  };

  // Excel indirme fonksiyonu - Demirbaş Durumu
  const exportStatusToExcel = () => {
    const headers = ['Tarih', 'Ürün Adı', 'Marka', 'Model', 'Seri No', 'Durum', 'Şube/Konum', 'Kullanıcı', 'Yanıt Durumu'];
    const rows = filteredZimmetler.map(z => [
      z.created_at ? new Date(z.created_at).toLocaleDateString('tr-TR') : '',
      z.urun_adi || '',
      z.marka || '',
      z.model || '',
      z.seri_no || '',
      z.durum || '',
      z.location || z.branch_name || '',
      z.kullanici_adi || '',
      z.kabul_durumu === 'pending' ? 'Bekliyor' : z.kabul_durumu === 'accepted' ? 'Teslim Aldı' : z.kabul_durumu === 'rejected' ? 'Reddedildi' : ''
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `demirbas_durumu_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Demirbaş durumu indirildi');
  };

  // Sıralama ikonu
  const SortIcon = ({ columnKey, sortConfig }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  // Stats
  const totalCount = demirbaslar.length;
  const totalValue = demirbaslar.reduce((sum, d) => sum + (d.total_price || 0), 0);
  const activeCount = demirbaslar.filter(d => d.status === 'active').length;
  const maintenanceCount = demirbaslar.filter(d => d.status === 'maintenance').length;
  const disposedCount = demirbaslar.filter(d => d.status === 'disposed').length;

  // Status table component
  const StatusTable = ({ items, bgColor }) => (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className={bgColor}>
            <TableHead className="cursor-pointer hover:bg-gray-50/50" onClick={() => handleStatusSort('product_name')}>
              <div className="flex items-center">
                Ürün Adı
                <SortIcon columnKey="product_name" sortConfig={statusSortConfig} />
              </div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50/50" onClick={() => handleStatusSort('serial_number')}>
              <div className="flex items-center">
                Seri No
                <SortIcon columnKey="serial_number" sortConfig={statusSortConfig} />
              </div>
            </TableHead>
            <TableHead className="text-center cursor-pointer hover:bg-gray-50/50" onClick={() => handleStatusSort('quantity')}>
              <div className="flex items-center justify-center">
                Miktar
                <SortIcon columnKey="quantity" sortConfig={statusSortConfig} />
              </div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50/50" onClick={() => handleStatusSort('location')}>
              <div className="flex items-center">
                Konum
                <SortIcon columnKey="location" sortConfig={statusSortConfig} />
              </div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50/50" onClick={() => handleStatusSort('usage_status')}>
              <div className="flex items-center">
                Son Durumu
                <SortIcon columnKey="usage_status" sortConfig={statusSortConfig} />
              </div>
            </TableHead>
            <TableHead className="cursor-pointer hover:bg-gray-50/50" onClick={() => handleStatusSort('inventory_date')}>
              <div className="flex items-center">
                Sayım Tarihi
                <SortIcon columnKey="inventory_date" sortConfig={statusSortConfig} />
              </div>
            </TableHead>
            <TableHead className="text-center">Detay</TableHead>
            <TableHead className="text-center">Geçmiş</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortData(items, statusSortConfig).map((demirbas) => (
            <TableRow key={demirbas.id}>
              <TableCell className="font-medium">{demirbas.product_name}</TableCell>
              <TableCell className="text-gray-500">{demirbas.serial_number || '-'}</TableCell>
              <TableCell className="text-center">{demirbas.quantity} {demirbas.unit}</TableCell>
              <TableCell className="text-gray-600">
                {demirbas.location || '-'}
                {demirbas.depot_receipt_status === 'pending' && (
                  <div className="mt-1">
                    <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-xs whitespace-normal" data-testid={`status-pending-${demirbas.id}`}>
                      {demirbas.pending_location
                        ? `⏳ ${demirbas.pending_location} konumuna gönderildi — teslim bekleniyor`
                        : '⏳ Depoda teslim bekleniyor'}
                    </Badge>
                  </div>
                )}
                {demirbas.depot_receipt_status === 'received' && demirbas.depot_received_by_name && (
                  <div className="mt-1">
                    <Badge className="bg-green-100 text-green-700 border-green-300 text-xs whitespace-normal">
                      ✓ Teslim alındı — {demirbas.depot_received_by_name}
                    </Badge>
                  </div>
                )}
              </TableCell>
              <TableCell className="text-gray-600 capitalize">
                {demirbas.usage_status === 'depoda' ? 'Depoda' :
                 demirbas.usage_status === 'kullanicida' ? 'Kullanıcıda' :
                 demirbas.usage_status === 'zimmette' ? 'Zimmette' :
                 demirbas.usage_status === 'bozuk' ? 'Bozuk' :
                 demirbas.usage_status === 'tamirde' ? 'Tamirde' :
                 demirbas.usage_status === 'kayip' ? 'Kayıp' :
                 demirbas.usage_status === 'demirbas' ? 'Demirbaş' :
                 (demirbas.usage_status || '-')}
              </TableCell>
              <TableCell className="text-gray-500">{demirbas.inventory_date ? formatDate(demirbas.inventory_date) : '-'}</TableCell>
              <TableCell className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleOpenStatusModal(demirbas)}
                  className="text-blue-600 hover:text-blue-800"
                  data-testid={`view-status-${demirbas.id}`}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TableCell>
              <TableCell className="text-center">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadStatusHistory(demirbas)}
                  className="text-purple-600 hover:text-purple-800"
                  data-testid={`view-history-${demirbas.id}`}
                  title="Durum Geçmişi"
                >
                  <History className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="purchasing-demirbaslar">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <ClipboardList className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-purple-600 font-medium">Toplam Demirbaş</p>
                <p className="text-2xl font-bold text-purple-700">{totalCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <DollarSign className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-emerald-600 font-medium">Toplam Değer</p>
                <p className="text-xl font-bold text-emerald-700">{formatCurrency(totalValue)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-blue-600 font-medium">Aktif Demirbaş</p>
                <p className="text-2xl font-bold text-blue-700">{activeCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Card>
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-4">
              <TabsList>
                <TabsTrigger value="list" className="gap-2">
                  <ClipboardList className="h-4 w-4" />
                  Demirbaş Listesi
                </TabsTrigger>
                <TabsTrigger value="status" className="gap-2">
                  <Activity className="h-4 w-4" />
                  Demirbaş Durumu
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Demirbaş Listesi Tab */}
            <TabsContent value="list" className="mt-0">
              <div className="mb-4 flex items-center justify-between gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Demirbaş ara (ad, seri no, tedarikçi, marka)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="demirbas-search"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={exportListToExcel}
                  className="gap-2"
                  data-testid="export-list-excel"
                >
                  <Download className="h-4 w-4" />
                  Excel İndir
                </Button>
              </div>

              {loading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : filteredDemirbaslar.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ClipboardList className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>Henüz demirbaş kaydı yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('product_name')}>
                          <div className="flex items-center">
                            Ürün Adı
                            <SortIcon columnKey="product_name" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('brand')}>
                          <div className="flex items-center">
                            Marka
                            <SortIcon columnKey="brand" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('supplier')}>
                          <div className="flex items-center">
                            Tedarikçi
                            <SortIcon columnKey="supplier" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('serial_number')}>
                          <div className="flex items-center">
                            Seri No
                            <SortIcon columnKey="serial_number" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="text-center cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('quantity')}>
                          <div className="flex items-center justify-center">
                            Miktar
                            <SortIcon columnKey="quantity" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('purchase_price')}>
                          <div className="flex items-center justify-end">
                            Birim Fiyat
                            <SortIcon columnKey="purchase_price" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="text-right cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('total_price')}>
                          <div className="flex items-center justify-end">
                            Toplam
                            <SortIcon columnKey="total_price" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('status')}>
                          <div className="flex items-center">
                            Durum
                            <SortIcon columnKey="status" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleListSort('created_at')}>
                          <div className="flex items-center">
                            Tarih
                            <SortIcon columnKey="created_at" sortConfig={listSortConfig} />
                          </div>
                        </TableHead>
                        <TableHead className="text-center">Belgeler</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortData(filteredDemirbaslar, listSortConfig).map((demirbas) => (
                        <TableRow key={demirbas.id} data-testid={`demirbas-row-${demirbas.id}`}>
                          <TableCell className="font-medium">{demirbas.product_name}</TableCell>
                          <TableCell className="text-gray-600">{demirbas.brand || '-'}</TableCell>
                          <TableCell className="text-gray-500 text-sm">{demirbas.supplier || demirbas.cari_hesap_name || '-'}</TableCell>
                          <TableCell className="text-gray-500 text-sm">{demirbas.serial_number || '-'}</TableCell>
                          <TableCell className="text-center">{demirbas.quantity} {demirbas.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(demirbas.purchase_price)}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(demirbas.total_price)}</TableCell>
                          <TableCell>{getStatusBadge(demirbas.status)}</TableCell>
                          <TableCell className="text-sm text-gray-500">{formatDate(demirbas.purchase_date)}</TableCell>
                          <TableCell className="text-center">
                            {demirbas.documents && demirbas.documents.length > 0 ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleViewDetail(demirbas)}
                                className="text-blue-600"
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                {demirbas.documents.length}
                              </Button>
                            ) : (
                              <span className="text-gray-400 text-sm">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </TabsContent>

            {/* Demirbaş Durumu Tab */}
            <TabsContent value="status" className="mt-0">
              {/* Filtreleme ve Excel Butonu */}
              <div className="mb-4 flex flex-wrap items-center gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Ara... (ürün, marka, seri no, kullanıcı)"
                    value={zimmetSearchTerm}
                    onChange={(e) => setZimmetSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="status-search"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-gray-500" />
                  <Select value={statusLocationFilter} onValueChange={setStatusLocationFilter}>
                    <SelectTrigger className="w-[200px]" data-testid="status-location-filter">
                      <SelectValue placeholder="Konum filtrele" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Konumlar</SelectItem>
                      {uniqueLocations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  variant="outline"
                  onClick={exportStatusToExcel}
                  className="gap-2"
                  data-testid="export-status-excel"
                >
                  <Download className="h-4 w-4" />
                  Excel İndir
                </Button>
              </div>
              
              {/* Zimmetler Tablosu (Depo Paneli Görünümü) */}
              <div className="space-y-6">
                <div className="border rounded-lg overflow-hidden bg-white">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50/50">
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('created_at')}
                        >
                          <div className="flex items-center">Tarih {getZimmetSortIcon('created_at')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('urun_adi')}
                        >
                          <div className="flex items-center">Ürün Adı {getZimmetSortIcon('urun_adi')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('marka')}
                        >
                          <div className="flex items-center">Marka {getZimmetSortIcon('marka')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('model')}
                        >
                          <div className="flex items-center">Model {getZimmetSortIcon('model')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('seri_no')}
                        >
                          <div className="flex items-center">Seri No {getZimmetSortIcon('seri_no')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('durum')}
                        >
                          <div className="flex items-center">Durum {getZimmetSortIcon('durum')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('location')}
                        >
                          <div className="flex items-center">Şube/Konum {getZimmetSortIcon('location')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('kullanici_adi')}
                        >
                          <div className="flex items-center">Kullanıcı {getZimmetSortIcon('kullanici_adi')}</div>
                        </TableHead>
                        <TableHead 
                          className="cursor-pointer hover:bg-gray-100"
                          onClick={() => handleZimmetSort('kabul_durumu')}
                        >
                          <div className="flex items-center">Yanıt Durumu {getZimmetSortIcon('kabul_durumu')}</div>
                        </TableHead>
                        <TableHead className="text-right">Geçmiş</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {zimmetLoading ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8">Yükleniyor...</TableCell>
                        </TableRow>
                      ) : filteredZimmetler.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-gray-500">Kayıt bulunamadı.</TableCell>
                        </TableRow>
                      ) : (
                        filteredZimmetler.map((zimmet) => (
                          <TableRow key={zimmet.id}>
                            <TableCell>
                              <div className="flex flex-col">
                                <span>{new Date(zimmet.created_at).toLocaleDateString('tr-TR')}</span>
                                <span className="text-xs text-gray-500">{new Date(zimmet.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-gray-900">{zimmet.urun_adi}</div>
                              {zimmet.depot_receipt_status === 'pending' && (
                                <Badge className="bg-orange-100 text-orange-700 border-orange-300 text-[10px] mt-1">
                                  {zimmet.pending_location ? `⏳ ${zimmet.pending_location} teslim bekleniyor` : '⏳ Depoda teslim bekleniyor'}
                                </Badge>
                              )}
                              {zimmet.depot_receipt_status === 'received' && (
                                <Badge className="bg-green-100 text-green-700 border-green-300 text-[10px] mt-1">
                                  Teslim Alındı — {zimmet.depot_received_by_name}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>{zimmet.marka || '-'}</TableCell>
                            <TableCell>{zimmet.model || '-'}</TableCell>
                            <TableCell>{zimmet.seri_no || '-'}</TableCell>
                            <TableCell>
                              <span className="capitalize">{zimmet.durum || '-'}</span>
                            </TableCell>
                            <TableCell>
                              <span className="text-gray-600 font-medium">
                                {zimmet.location || zimmet.branch_name || '-'}
                              </span>
                            </TableCell>
                            <TableCell>
                              {zimmet.kullanici_adi ? (
                                <div className="flex flex-col">
                                  <span className="font-medium text-blue-700">{zimmet.kullanici_adi}</span>
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              {zimmet.kabul_durumu === 'pending' ? (
                                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">Bekliyor</Badge>
                              ) : zimmet.kabul_durumu === 'accepted' ? (
                                <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Teslim Aldı</Badge>
                              ) : zimmet.kabul_durumu === 'rejected' ? (
                                <div className="flex flex-col gap-1 items-start">
                                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Reddedildi</Badge>
                                  {zimmet.red_sebebi && <span className="text-xs text-red-600 truncate max-w-[150px]" title={zimmet.red_sebebi}>{zimmet.red_sebebi}</span>}
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => loadStatusHistory(zimmet)}
                                className="text-purple-600 hover:text-purple-800"
                                data-testid={`view-history-${zimmet.id}`}
                              >
                                <Clock className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-purple-600" />
              {editingDemirbas ? 'Demirbaş Düzenle' : 'Yeni Demirbaş'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {editingDemirbas ? (
              <>
                <div className="space-y-2">
                  <Label>Ürün Adı *</Label>
                  <Input
                    value={formData.product_name}
                    onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                    placeholder="Demirbaş adı"
                    data-testid="demirbas-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Miktar</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 1 })}
                      data-testid="demirbas-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Birim</Label>
                    <Select
                      value={formData.unit}
                      onValueChange={(value) => setFormData({ ...formData, unit: value })}
                    >
                      <SelectTrigger data-testid="demirbas-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="adet">Adet</SelectItem>
                        <SelectItem value="set">Set</SelectItem>
                        <SelectItem value="takım">Takım</SelectItem>
                        <SelectItem value="kg">Kg</SelectItem>
                        <SelectItem value="metre">Metre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Alış Fiyatı (₺)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({ ...formData, purchase_price: parseFloat(e.target.value) || 0 })}
                    data-testid="demirbas-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Marka</Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Ürün markası"
                    data-testid="demirbas-brand-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Seri Numarası</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="Seri/Model numarası"
                    data-testid="demirbas-serial-input"
                  />
                </div>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Ürünler ({(formData.products || []).length})</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addProduct} data-testid="demirbas-add-product-btn">
                    <Plus className="h-4 w-4 mr-1" /> Ürün Ekle
                  </Button>
                </div>
                {(formData.products || []).map((p, idx) => (
                  <div key={idx} className="border rounded-lg p-3 space-y-3 bg-gray-50" data-testid={`demirbas-product-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-purple-700">Ürün {idx + 1}</span>
                      {(formData.products || []).length > 1 && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeProduct(idx)} className="h-6 w-6 p-0 text-red-500 hover:text-red-700" data-testid={`demirbas-remove-product-${idx}`}>
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>Ürün Adı *</Label>
                      <Input
                        value={p.product_name}
                        onChange={(e) => updateProduct(idx, 'product_name', e.target.value)}
                        placeholder="Demirbaş adı"
                        data-testid={`demirbas-name-input-${idx}`}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Miktar</Label>
                        <Input
                          type="number"
                          min="1"
                          value={p.quantity}
                          onChange={(e) => updateProduct(idx, 'quantity', parseFloat(e.target.value) || 1)}
                          data-testid={`demirbas-quantity-input-${idx}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Birim</Label>
                        <Select value={p.unit} onValueChange={(value) => updateProduct(idx, 'unit', value)}>
                          <SelectTrigger data-testid={`demirbas-unit-select-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="adet">Adet</SelectItem>
                            <SelectItem value="set">Set</SelectItem>
                            <SelectItem value="takım">Takım</SelectItem>
                            <SelectItem value="kg">Kg</SelectItem>
                            <SelectItem value="metre">Metre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Alış Fiyatı (₺)</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={p.purchase_price}
                          onChange={(e) => updateProduct(idx, 'purchase_price', parseFloat(e.target.value) || 0)}
                          data-testid={`demirbas-price-input-${idx}`}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Marka</Label>
                        <Input
                          value={p.brand}
                          onChange={(e) => updateProduct(idx, 'brand', e.target.value)}
                          placeholder="Ürün markası"
                          data-testid={`demirbas-brand-input-${idx}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Seri Numarası</Label>
                      <Input
                        value={p.serial_number}
                        onChange={(e) => updateProduct(idx, 'serial_number', e.target.value)}
                        placeholder="Seri/Model numarası"
                        data-testid={`demirbas-serial-input-${idx}`}
                      />
                    </div>
                  </div>
                ))}
                <Button type="button" variant="outline" className="w-full border-dashed" onClick={addProduct} data-testid="demirbas-add-product-btn-bottom">
                  <Plus className="h-4 w-4 mr-1" /> Yeni Ürün Ekle
                </Button>
              </div>
            )}

            <div className="space-y-2">
              <Label>Cari Hesap</Label>
              <Select
                value={formData.supplier_id}
                onValueChange={(value) => {
                  const selectedCari = cariHesaplar.find(c => c.id === value);
                  setFormData({ 
                    ...formData, 
                    supplier_id: value,
                    supplier: selectedCari?.firma_adi || selectedCari?.ad || ''
                  });
                }}
              >
                <SelectTrigger data-testid="demirbas-supplier-select">
                  <SelectValue placeholder="Cari hesap seçin" />
                </SelectTrigger>
                <SelectContent>
                  {cariHesaplar.map((cari) => (
                    <SelectItem key={cari.id} value={cari.id}>
                      {cari.firma_adi || cari.ad} {cari.firma_adi && cari.ad ? `(${cari.ad})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Durum</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger data-testid="demirbas-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Aktif</SelectItem>
                  <SelectItem value="maintenance">Bakımda</SelectItem>
                  <SelectItem value="disposed">Elden Çıkarıldı</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Açıklama</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Ek açıklamalar..."
                rows={2}
                data-testid="demirbas-description-input"
              />
            </div>

            {/* Fatura Belgesi Bölümü */}
            <div className="space-y-2 p-4 bg-orange-50 rounded-lg border border-orange-200">
              <Label className="text-orange-700 font-medium">Fatura Belgesi</Label>
              <div className="border-2 border-dashed border-orange-300 rounded-lg p-3 bg-white">
                <input
                  type="file"
                  multiple
                  onChange={handleInvoiceFileUpload}
                  className="hidden"
                  id="invoice-files"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                />
                <label htmlFor="invoice-files" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-6 w-6 text-orange-400 mb-1" />
                  <span className="text-sm text-orange-600">Fatura yükle</span>
                  <span className="text-xs text-orange-400 mt-1">PDF, Word, Resim</span>
                </label>
              </div>
              
              {formData.invoice_documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {formData.invoice_documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-orange-200">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <div className="min-w-0">
                          <span className="text-sm truncate block max-w-[180px]">{doc.name}</span>
                          {doc.uploaded_at && (
                            <span className="text-xs text-gray-400">Yüklenme: {formatDate(doc.uploaded_at)}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {doc.data && (
                          <a
                            href={doc.data}
                            download={doc.name}
                            className="text-orange-600 hover:text-orange-800 p-1"
                          >
                            <Download className="h-4 w-4" />
                          </a>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveInvoiceDocument(index)}
                          className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Belgeler Bölümü */}
            <div className="space-y-2">
              <Label>Diğer Belgeler</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="demirbas-files"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <label htmlFor="demirbas-files" className="flex flex-col items-center cursor-pointer">
                  <Upload className="h-8 w-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Dosya yüklemek için tıklayın</span>
                  <span className="text-xs text-gray-400 mt-1">PDF, Word, Excel, Resim</span>
                </label>
              </div>
              
              {formData.documents.length > 0 && (
                <div className="space-y-2 mt-2">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-500" />
                        <span className="text-sm truncate max-w-[200px]">{doc.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveDocument(index)}
                        className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleSubmit} 
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="demirbas-submit-btn"
            >
              {editingDemirbas ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Edit Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Demirbaş Durumu - {selectedDemirbas?.product_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Teslim durumu bilgilendirmesi */}
            {selectedDemirbas?.depot_receipt_status === 'pending' && (
              <div className="flex items-start gap-2 rounded-lg border border-orange-300 bg-orange-50 p-3" data-testid="status-receipt-info-pending">
                <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5 shrink-0" />
                <p className="text-sm text-orange-800">
                  {selectedDemirbas?.pending_location
                    ? <><strong>{selectedDemirbas.pending_location}</strong> konumuna gönderildi, ancak ilgili depo henüz <strong>teslim almadı</strong>. Konum, karşı taraf "Teslim Al" onayı verene kadar değişmeyecek; şu anki konum: <strong>{selectedDemirbas.location || '-'}</strong>.</>
                    : <>Bu demirbaş depoda <strong>teslim bekliyor</strong>. İlgili depocu "Teslim Al" onayı verene kadar bekleyecek.</>}
                </p>
              </div>
            )}
            {selectedDemirbas?.depot_receipt_status === 'received' && selectedDemirbas?.depot_received_by_name && (
              <div className="flex items-start gap-2 rounded-lg border border-green-300 bg-green-50 p-3" data-testid="status-receipt-info-received">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
                <p className="text-sm text-green-800">
                  <strong>{selectedDemirbas.depot_received_by_name}</strong> tarafından teslim alındı
                  {selectedDemirbas.depot_received_at ? ` (${formatDate(selectedDemirbas.depot_received_at)})` : ''}.
                </p>
              </div>
            )}

            {/* Konum */}
            <div className="space-y-2">
              <Label>Konum</Label>
              <Select
                value={statusFormData.location}
                onValueChange={(value) => setStatusFormData({ ...statusFormData, location: value, location_other: value === 'other' ? statusFormData.location_other : '' })}
              >
                <SelectTrigger data-testid="status-location-select">
                  <SelectValue placeholder="Konum seçin" />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.name}>
                      {branch.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Diğer (Elle Yaz)</SelectItem>
                </SelectContent>
              </Select>
              {statusFormData.location === 'other' && (
                <Input
                  value={statusFormData.location_other}
                  onChange={(e) => setStatusFormData({ ...statusFormData, location_other: e.target.value })}
                  placeholder="Konum giriniz..."
                  className="mt-2"
                  data-testid="status-location-other-input"
                />
              )}
              <p className="text-xs text-gray-500">
                Not: Konumu değiştirdiğinizde demirbaş hemen taşınmaz. Yeni konumun deposu "Teslim Al" onayı verene kadar mevcut konumda kalır.
              </p>
            </div>

            {/* Kullanım Durumu (Son Durumu) */}
            <div className="space-y-2">
              <Label>Son Durumu (Depo/Kullanım)</Label>
              <Select
                value={statusFormData.usage_status}
                onValueChange={(value) => setStatusFormData({ ...statusFormData, usage_status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Durum seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="depoda">Depoda</SelectItem>
                  <SelectItem value="kullanicida">Kullanıcıda</SelectItem>
                  <SelectItem value="zimmette">Zimmette</SelectItem>
                  <SelectItem value="bozuk">Bozuk</SelectItem>
                  <SelectItem value="tamirde">Tamirde</SelectItem>
                  <SelectItem value="kayip">Kayıp</SelectItem>
                  <SelectItem value="demirbas">Demirbaş</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Sayım Tarihi */}
            <div className="space-y-2">
              <Label>Demirbaş Sayım Tarihi</Label>
              <Input
                type="date"
                value={statusFormData.inventory_date}
                onChange={(e) => setStatusFormData({ ...statusFormData, inventory_date: e.target.value })}
                data-testid="status-inventory-date-input"
              />
            </div>

            {/* Durum Notu / Açıklama */}
            <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
              <Label className="text-base font-semibold">Durum Notu ve Belgeler</Label>
              
              <div className="space-y-3 mt-2">
                <div>
                  <Label className="text-sm">Açıklama</Label>
                  <Textarea
                    value={statusFormData.last_status_description}
                    onChange={(e) => setStatusFormData({ ...statusFormData, last_status_description: e.target.value })}
                    placeholder="Durum açıklaması..."
                    rows={3}
                    data-testid="status-description-input"
                  />
                </div>

                {/* Belgeler */}
                <div>
                  <Label className="text-sm">Belgeler</Label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 mt-1">
                    <input
                      type="file"
                      multiple
                      onChange={handleStatusFileUpload}
                      className="hidden"
                      id="status-files"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                    />
                    <label htmlFor="status-files" className="flex flex-col items-center cursor-pointer">
                      <Upload className="h-6 w-6 text-gray-400 mb-1" />
                      <span className="text-sm text-gray-500">Belge ekle</span>
                    </label>
                  </div>
                  
                  {statusFormData.last_status_documents.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {statusFormData.last_status_documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <span className="text-sm truncate block max-w-[180px]">{doc.name}</span>
                              {doc.uploaded_at && (
                                <span className="text-xs text-gray-400">Yüklenme: {formatDate(doc.uploaded_at)}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {doc.data && (
                              <a
                                href={doc.data}
                                download={doc.name}
                                className="text-blue-600 hover:text-blue-800 p-1"
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveStatusDocument(index)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusModal(false)}>
              İptal
            </Button>
            <Button 
              onClick={handleStatusSubmit} 
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="status-submit-btn"
            >
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Modal for viewing documents */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              {selectedDemirbas?.product_name} - Belgeler
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {selectedDemirbas?.documents && selectedDemirbas.documents.length > 0 ? (
              <div className="space-y-3">
                {selectedDemirbas.documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">{doc.name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.size ? `${(doc.size / 1024).toFixed(1)} KB` : ''}
                          {doc.uploaded_at ? ` • ${formatDate(doc.uploaded_at)}` : ''}
                        </p>
                      </div>
                    </div>
                    {doc.data && (
                      <a
                        href={doc.data}
                        download={doc.name}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <Download className="h-5 w-5" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">Belge bulunmuyor</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Durum Geçmişi Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-600" />
              Durum Geçmişi - {selectedHistoryDemirbas?.product_name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              </div>
            ) : historyData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <History className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>Henüz durum geçmişi bulunmuyor</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-200"></div>
                
                <div className="space-y-6">
                  {historyData.map((entry, index) => (
                    <div key={entry.id || index} className="relative pl-10">
                      {/* Timeline dot */}
                      <div className={`absolute left-2 w-5 h-5 rounded-full border-2 ${
                        index === 0 
                          ? 'bg-purple-600 border-purple-600' 
                          : 'bg-white border-purple-400'
                      }`}>
                        {index === 0 && (
                          <div className="absolute inset-1 bg-white rounded-full"></div>
                        )}
                      </div>
                      
                      {/* Content Card */}
                      <div className={`rounded-lg border p-4 ${
                        index === 0 
                          ? 'bg-purple-50 border-purple-200' 
                          : 'bg-gray-50 border-gray-200'
                      }`}>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="font-medium text-gray-700">
                              {entry.date ? new Date(entry.date).toLocaleString('tr-TR') : '-'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            <User className="h-4 w-4" />
                            <span>{entry.user_name || 'Bilinmiyor'}</span>
                          </div>
                        </div>
                        
                        {/* Changes */}
                        {entry.changes && entry.changes.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm font-medium text-gray-600 mb-1">Değişiklikler:</p>
                            <ul className="space-y-1">
                              {entry.changes.map((change, i) => (
                                <li key={i} className="text-sm text-gray-700 flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-purple-500"></span>
                                  {change}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {/* Details Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {entry.status && (
                            <div>
                              <span className="text-gray-500 flex items-center gap-1">
                                <Activity className="h-3 w-3" /> Durum
                              </span>
                              <Badge className={
                                entry.status === 'active' ? 'bg-green-100 text-green-700' :
                                entry.status === 'maintenance' ? 'bg-yellow-100 text-yellow-700' :
                                entry.status === 'disposed' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {entry.status === 'active' ? 'Aktif' :
                                 entry.status === 'maintenance' ? 'Bakımda' :
                                 entry.status === 'disposed' ? 'Elden Çıkarıldı' :
                                 entry.status}
                              </Badge>
                            </div>
                          )}
                          {entry.location && (
                            <div>
                              <span className="text-gray-500 flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> Konum
                              </span>
                              <p className="font-medium">{entry.location}</p>
                            </div>
                          )}
                          {entry.last_status && (
                            <div>
                              <span className="text-gray-500">Son Durumu</span>
                              <p className="font-medium">{entry.last_status}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Additional Info */}
                        {(entry.brand || entry.serial_number || entry.inventory_date) && (
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mt-2 pt-2 border-t border-gray-200">
                            {entry.brand && (
                              <div>
                                <span className="text-gray-500">Marka</span>
                                <p className="font-medium">{entry.brand}</p>
                              </div>
                            )}
                            {entry.serial_number && (
                              <div>
                                <span className="text-gray-500">Seri No</span>
                                <p className="font-medium">{entry.serial_number}</p>
                              </div>
                            )}
                            {entry.inventory_date && (
                              <div>
                                <span className="text-gray-500">Envanter Tarihi</span>
                                <p className="font-medium">{new Date(entry.inventory_date).toLocaleDateString('tr-TR')}</p>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Description */}
                        {entry.description && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-600 mb-1">Açıklama:</p>
                            <p className="text-sm text-gray-600">{entry.description}</p>
                          </div>
                        )}
                        
                        {/* Documents */}
                        {entry.documents && entry.documents.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <FileText className="h-4 w-4" /> Eklenen Belgeler ({entry.documents.length})
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {entry.documents.map((doc, docIdx) => {
                                // doc string (URL), base64 object veya path object olabilir
                                let docUrl = '';
                                let docName = 'Belge';
                                let isBase64 = false;
                                
                                if (typeof doc === 'string') {
                                  // Düz string URL
                                  docUrl = doc.startsWith('/') ? `${API}${doc}` : doc;
                                  docName = doc.split('/').pop() || 'Belge';
                                } else if (doc?.data && doc.data.startsWith('data:')) {
                                  // Base64 encoded belge
                                  docUrl = doc.data;
                                  docName = doc.name || 'Belge';
                                  isBase64 = true;
                                } else if (doc?.url || doc?.path) {
                                  // URL veya path object
                                  const path = doc.url || doc.path;
                                  docUrl = path.startsWith('/') ? `${API}${path}` : path;
                                  docName = doc.name || doc.filename || path.split('/').pop() || 'Belge';
                                }
                                
                                if (!docUrl) return null;
                                
                                const isImage = isBase64 
                                  ? doc?.type?.startsWith('image/') 
                                  : (docUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) !== null);
                                
                                return (
                                  <a
                                    key={docIdx}
                                    href={docUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download={isBase64 ? docName : undefined}
                                    className="flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs hover:bg-blue-100 transition-colors"
                                  >
                                    {isImage ? <Eye className="h-3 w-3" /> : <Download className="h-3 w-3" />}
                                    {docName.length > 20 ? docName.substring(0, 17) + '...' : docName}
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingDemirbaslar;
